import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import OrderManagementTable from '../../containers/OrderManagemetTable/OrderManagemetTable';
import OrderManagementTabs from '../../components/OrderManagementTabs/OrderManagementTabs';
import AdminOrdersFilterBar from '../../containers/AdminPortalFilterBarContainer/AdminPortalFilterBarContainer';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from './OrderManagement.styles';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { exportOrders, getOrderById } from '../../api/api';
import {useToast} from '../../components/ToastProvider/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';

export default function OrderManagement() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('orders.manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', priority: '', payment_status: '' });
  const refreshKeyRef = useRef(0);

  const orderIdFromUrl = searchParams.get('orderId');
  const showListToolbar = !selectedOrder && !orderIdFromUrl;

  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (!orderId || selectedOrder) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const order = await getOrderById(orderId);
        if (cancelled) return;
        if (order) {
          setSelectedOrder(order);
        } else {
          showToast('Order not found', 'error');
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('orderId');
            return next;
          });
        }
      } catch (e) {
        if (cancelled) return;
        showToast(e?.message || 'Could not load order', 'error');
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('orderId');
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, selectedOrder, setSearchParams, showToast]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setTabIndex(0);
  };

  const handleExport = async () => {
    try {
      const blob = await exportOrders(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      const isCSV = blob.type.includes('text/csv');
      a.download = `orders_export_${today}.${isCSV ? 'csv' : 'xlsx'}`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Orders exported successfully', 'success'); 
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Failed to export orders', 'error'); 
    }
  };

  const handleRefresh = () => {
    // Force refresh by updating key, which will trigger OrderManagementTable to re-fetch
    refreshKeyRef.current += 1;
    setFilters(prev => ({ ...prev })); // Trigger re-render with same filters to force refresh
    showToast('Refreshing data...', 'info');
  };

  return (
    <>
      <TopNavbar onTabChange={setTabIndex} />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          {showListToolbar && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Box flex={1}>
              <AdminOrdersFilterBar
                value={filters}
                onChange={(next) => setFilters(next)}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Refresh
            </Button>
            {canEdit && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Export
            </Button>
            )}
          </Box>
          )}

          {selectedOrder ? (
            <OrderManagementTabs embedded order={selectedOrder} defaultTab={tabIndex} readOnly={!canEdit} />
          ) : orderIdFromUrl ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
              <CircularProgress size={36} sx={{ color: '#002F87' }} />
              <Typography variant="body2" color="text.secondary">
                Loading order…
              </Typography>
            </Box>
          ) : (
            <OrderManagementTable key={refreshKeyRef.current} onView={handleViewOrder} filters={filters} readOnly={!canEdit} />
          )}
        </ContentContainer>
      </PageContainer>
    </>
  );
}
